import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
  type Variants,
} from "framer-motion";

import { CurrentLeagueNamingProvider } from "@/components/league";
import { useCurrentLeague } from "@/contexts";
import { useBreakpoint } from "@/hooks/userBreakPoint";
import { DialogTransition, Divider, Drawer, ScrollShadow } from "@/ui";

import { NavLink, useNavLinkItemFilter } from "../NavLink";

import { GroupsList } from "./GroupsList";
import { LeagueGroupsList } from "./LeagueNav";
import { LeagueOnboardingBanner } from "./LeagueOnboardingBanner";
import { LeagueSidebarHeader } from "./LeagueSidebarHeader";
import { OnboardingBanner } from "./OnboardingBanner";
import { PlatformSelect } from "./PlatformSelect";

type DashboardSidebarProps = {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

// Subtle, fast ease-out — matches the app's motion language (see documentsMotion).
const modeSwitchTransition: Transition = { duration: 0.18, ease: [0.16, 1, 0.3, 1] };

// Per-panel slide direction is fixed so the swap reads as a drill-down: the league
// panel always enters/leaves on the right, the default panel on the left. With
// `mode="wait"` that makes "enter league" feel forward and "back" feel backward,
// without tracking the switch direction at runtime.
const leagueModeVariants: Variants = {
  initial: { opacity: 0, x: 12 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 12 },
};

const defaultModeVariants: Variants = {
  initial: { opacity: 0, x: -12 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -12 },
};

const SidebarContent = () => {
  const filterNavLinkItem = useNavLinkItemFilter();
  const league = useCurrentLeague();
  const reducedMotion = useReducedMotion();
  const transition = reducedMotion ? { duration: 0 } : modeSwitchTransition;

  return (
    <>
      <PlatformSelect />
      {/* `initial={false}` so a deep-link straight into a league doesn't animate on
          mount — only an actual mode switch animates. `mode="wait"` swaps the panels
          one at a time so their differing heights don't overlap mid-transition. */}
      <AnimatePresence mode="wait" initial={false}>
        {league ? (
          <motion.div
            key="league"
            variants={leagueModeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={transition}
          >
            {/* The league panel renders for both container types, so its wording
                follows the scoped league's own `tourType` (stages vs tournaments). */}
            <CurrentLeagueNamingProvider>
              <LeagueSidebarHeader league={league} />
              <LeagueOnboardingBanner leagueId={league.id} className="mt-3" />
              <Divider className="mt-4" />
              <LeagueGroupsList league={league} />
            </CurrentLeagueNamingProvider>
          </motion.div>
        ) : (
          <motion.div
            key="default"
            variants={defaultModeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={transition}
          >
            <OnboardingBanner className="mt-3" />
            <div className="flex flex-col mt-4">
              {filterNavLinkItem("home") && <NavLink item={{ type: "home" }} actions={[]} />}
              <NavLink item={{ type: "documentation" }} actions={[]} />
            </div>
            <Divider className="mt-4" />
            <GroupsList />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const DashboardSidebar = ({ isOpen, onOpenChange }: DashboardSidebarProps) => {
  const isMediumScreen = useBreakpoint("md", "down");
  const isSmallScreen = useBreakpoint("sm", "down");

  if (isMediumScreen) {
    return (
      <DialogTransition>
        {isOpen && (
          <Drawer
            hideCloseButton={!isSmallScreen}
            onOpenChange={onOpenChange}
            size={isSmallScreen ? "full" : 252}
          >
            <ScrollShadow orientation="vertical" hideScrollBar>
              <SidebarContent />
            </ScrollShadow>
          </Drawer>
        )}
      </DialogTransition>
    );
  }

  return (
    <ScrollShadow
      orientation="vertical"
      hideScrollBar
      className="bg-surface-1 w-[252px] p-[10px] border-r border-surface-2"
    >
      <SidebarContent />
    </ScrollShadow>
  );
};
